import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Check, Truck, MapPin, Phone, Clock } from 'lucide-react';

interface AIReadabilityBlockProps {
  variant?: 'home' | 'services' | 'products';
}

/**
 * AIReadabilityBlock - Optimized content block for AI/LLM extraction
 * This component provides structured, factual information that AI search engines
 * can easily parse and understand. It follows LLM readability best practices:
 * - Clear, concise bullet points
 * - Factual information
 * - Structured data format
 * - No dependency on images for critical info
 */
const AIReadabilityBlock: React.FC<AIReadabilityBlockProps> = ({ variant = 'home' }) => {
  const { language } = useLanguage();

  const content = {
    en: {
      home: {
        title: 'Quick Facts About The Grass Depot',
        subtitle: 'Your trusted landscaping materials supplier in Texas',
        bullets: [
          'Premium sod, soil, rock, mulch & sand delivery in Montgomery County TX',
          'Same-day delivery available for orders before 2 PM',
          'Serving Conroe, The Woodlands, Spring, Magnolia, Tomball & more',
          'Wholesale pricing for contractors and homeowners',
          'Bilingual service in English and Spanish'
        ],
        whatWeDo: {
          title: 'What We Do',
          content: 'The Grass Depot LLC supplies premium landscaping materials including sod, topsoil, compost, decorative rock, mulch, and sand. We deliver to residential and commercial customers throughout Montgomery County, Texas.'
        },
        forWhom: {
          title: 'Who We Serve',
          content: 'Homeowners, landscaping contractors, property managers, golf courses, and commercial developers trust us for quality materials and reliable delivery.'
        },
        howItWorks: {
          title: 'How It Works',
          steps: [
            'Use our free online calculator to estimate your materials',
            'Call or submit a quote request for pricing',
            'Schedule your delivery - same-day available',
            'We deliver directly to your project site'
          ]
        }
      },
      services: {
        title: 'Our Delivery Services',
        subtitle: 'Professional landscaping material delivery in Texas',
        bullets: [
          'Sod delivery - pallets delivered fresh from the farm',
          'Bulk material delivery - soil, rock, mulch, sand by the cubic yard',
          'Same-day and next-day delivery options',
          'Commercial and residential service',
          'Delivery within 50 miles of Spring, TX'
        ]
      },
      products: {
        title: 'Available Materials',
        subtitle: 'Premium landscaping supplies for Texas properties',
        bullets: [
          'Sod: St. Augustine, Bermuda, Zoysia varieties',
          'Soil: Topsoil, compost, garden mix, fill dirt',
          'Rock: Decorative stone, gravel, limestone, river rock',
          'Mulch: Hardwood, cedar, pine, colored options',
          'Sand: Masonry sand, play sand, fill sand'
        ]
      }
    },
    es: {
      home: {
        title: 'Datos Rápidos Sobre The Grass Depot',
        subtitle: 'Su proveedor de confianza de materiales de paisajismo en Texas',
        bullets: [
          'Entrega de césped premium, tierra, roca, mantillo y arena en el Condado Montgomery TX',
          'Entrega el mismo día disponible para pedidos antes de las 2 PM',
          'Servicio en Conroe, The Woodlands, Spring, Magnolia, Tomball y más',
          'Precios mayoristas para contratistas y propietarios',
          'Servicio bilingüe en inglés y español'
        ],
        whatWeDo: {
          title: 'Qué Hacemos',
          content: 'The Grass Depot LLC suministra materiales de paisajismo premium incluyendo césped, tierra vegetal, composta, roca decorativa, mantillo y arena. Entregamos a clientes residenciales y comerciales en todo el Condado Montgomery, Texas.'
        },
        forWhom: {
          title: 'A Quién Servimos',
          content: 'Propietarios de viviendas, contratistas de paisajismo, administradores de propiedades, campos de golf y desarrolladores comerciales confían en nosotros para materiales de calidad y entrega confiable.'
        },
        howItWorks: {
          title: 'Cómo Funciona',
          steps: [
            'Use nuestra calculadora gratuita en línea para estimar sus materiales',
            'Llame o envíe una solicitud de cotización para precios',
            'Programe su entrega - mismo día disponible',
            'Entregamos directamente a su sitio de proyecto'
          ]
        }
      },
      services: {
        title: 'Nuestros Servicios de Entrega',
        subtitle: 'Entrega profesional de materiales de paisajismo en Texas',
        bullets: [
          'Entrega de césped - pallets entregados frescos de la granja',
          'Entrega de materiales a granel - tierra, roca, mantillo, arena por yarda cúbica',
          'Opciones de entrega el mismo día y al día siguiente',
          'Servicio comercial y residencial',
          'Entrega dentro de 50 millas de Spring, TX'
        ]
      },
      products: {
        title: 'Materiales Disponibles',
        subtitle: 'Suministros de paisajismo premium para propiedades en Texas',
        bullets: [
          'Césped: Variedades St. Augustine, Bermuda, Zoysia',
          'Tierra: Tierra vegetal, composta, mezcla de jardín, relleno',
          'Roca: Piedra decorativa, grava, piedra caliza, piedra de río',
          'Mantillo: Madera dura, cedro, pino, opciones de colores',
          'Arena: Arena de albañilería, arena de juego, arena de relleno'
        ]
      }
    }
  };

  const currentContent = content[language][variant];
  const homeContent = content[language].home;

  return (
    <section className="py-12 bg-background border-y" aria-label={currentContent.title}>
      <div className="container mx-auto px-4">
        {/* Quick Facts Header */}
        <header className="mb-8">
          <h2 className="text-2xl font-bold text-foreground mb-2">
            {currentContent.title}
          </h2>
          <p className="text-muted-foreground">{currentContent.subtitle}</p>
        </header>

        {/* Key Bullets - Optimized for AI extraction */}
        <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10" role="list">
          {currentContent.bullets.map((bullet, index) => (
            <li key={index} className="flex items-start gap-3">
              <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" aria-hidden="true" />
              <span className="text-foreground">{bullet}</span>
            </li>
          ))}
        </ul>

        {/* Detailed Information Blocks - Only show for home variant */}
        {variant === 'home' && (
          <div className="grid md:grid-cols-3 gap-8 mt-10 pt-10 border-t">
            {/* What We Do */}
            <article>
              <h3 className="font-semibold text-lg text-foreground mb-3">
                {homeContent.whatWeDo.title}
              </h3>
              <p className="text-muted-foreground">
                {homeContent.whatWeDo.content}
              </p>
            </article>

            {/* Who We Serve */}
            <article>
              <h3 className="font-semibold text-lg text-foreground mb-3">
                {homeContent.forWhom.title}
              </h3>
              <p className="text-muted-foreground">
                {homeContent.forWhom.content}
              </p>
            </article>

            {/* How It Works */}
            <article>
              <h3 className="font-semibold text-lg text-foreground mb-3">
                {homeContent.howItWorks.title}
              </h3>
              <ol className="space-y-2 text-muted-foreground">
                {homeContent.howItWorks.steps.map((step, index) => (
                  <li key={index} className="flex gap-2">
                    <span className="font-medium text-primary">{index + 1}.</span>
                    {step}
                  </li>
                ))}
              </ol>
            </article>
          </div>
        )}

        {/* Contact Quick Reference */}
        <div className="mt-10 pt-8 border-t flex flex-wrap gap-6 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4" aria-hidden="true" />
            <a href="tel:+19364637939" className="hover:text-primary">(936) 463-7939</a>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4" aria-hidden="true" />
            <span>936 Sawdust Rd, Spring, TX 77380</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" aria-hidden="true" />
            <span>{language === 'es' ? 'Lun-Sáb 7AM-6PM' : 'Mon-Sat 7AM-6PM'}</span>
          </div>
          <div className="flex items-center gap-2">
            <Truck className="w-4 h-4" aria-hidden="true" />
            <span>{language === 'es' ? 'Entrega mismo día disponible' : 'Same-day delivery available'}</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIReadabilityBlock;
